/**
 * Analyzer: Setup Pattern Calibration (Wave C, v7.4.5)
 *
 * For each pattern type that fired on enough historical trades, compare
 * the WR + avg-R of trades-with-pattern vs trades-without-pattern. This
 * is the analytical payoff of the Wave C pattern library — answers
 * "do my Wolf Stack entries actually outperform?" with the trader's
 * own data.
 *
 * Surfaces ONE observation per pattern type that has a meaningful
 * sample + meaningful WR delta. Most days no observations fire;
 * accumulation over 30D produces the signal.
 *
 * Sample-size guards:
 *   - At least 8 trades with the pattern (a sub-week of meaningful data)
 *   - At least 8 trades without (so the baseline is real)
 *   - WR gap ≥ 15% in either direction (don't fire on noise)
 *
 * SUCCESS when pattern outperforms baseline.
 * WARNING when pattern UNDERPERFORMS — flags patterns that look good
 * in literature but don't work for this trader.
 *
 * Window: rolling 30 trading days.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { getTradingDay, addDaysToDateStr } from '../../../timeService.js';
import { PATTERN_CATALOG } from '../../../barAnalysis/setupPatterns.js';

export const key = 'setupPatternCalibration';
export const tier = 'free';

const MIN_PER_COHORT = 8;
const MIN_WR_GAP = 0.15;

const inWindow = (trade, fromDateStr, toDateStr) => {
  if (!trade?.date || typeof trade.date !== 'string') return false;
  return trade.date >= fromDateStr && trade.date <= toDateStr;
};

const isWinner = (trade) => Number.isFinite(parseFloat(trade?.pnl)) && parseFloat(trade.pnl) > 0;

const summarize = (trades) => {
  if (trades.length === 0) return { count: 0, wr: 0, avgR: 0 };
  const wins = trades.filter(isWinner).length;
  const rs = trades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : 0;
  return { count: trades.length, wr: wins / trades.length, avgR };
};

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

/**
 * Did this trade fire a pattern of `type`?
 * Reads `trade.barAnalysis.setupPatterns[]` populated by maeMfe.
 */
const tradeHasPattern = (trade, type) => {
  const patterns = trade?.barAnalysis?.setupPatterns;
  if (!Array.isArray(patterns)) return false;
  return patterns.some(p => p?.type === type);
};

export const analyze = (ctx) => {
  const { allTrades, currentTradingDay, windowDays = 30 } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;

  const anchor = currentTradingDay || getTradingDay(Date.now()) || null;
  if (!anchor) return null;
  const fromDateStr = addDaysToDateStr(anchor, -windowDays);
  const windowed = allTrades.filter(t => inWindow(t, fromDateStr, anchor));
  if (windowed.length < MIN_PER_COHORT * 2) return null;

  const observations = [];

  // For each pattern type in the catalog, compute the with/without comparison.
  for (const patternType of Object.keys(PATTERN_CATALOG)) {
    const withPattern = windowed.filter(t => tradeHasPattern(t, patternType));
    const withoutPattern = windowed.filter(t => !tradeHasPattern(t, patternType));
    if (withPattern.length < MIN_PER_COHORT || withoutPattern.length < MIN_PER_COHORT) continue;

    const w = summarize(withPattern);
    const wo = summarize(withoutPattern);
    const gap = w.wr - wo.wr;
    if (Math.abs(gap) < MIN_WR_GAP) continue;

    const label = PATTERN_CATALOG[patternType]?.label || patternType;

    if (gap > 0) {
      // Pattern outperforms — celebrate
      observations.push(makeObservation({
        id: `${key}:${anchor}:${patternType}:outperforms`,
        analyzer: key,
        scope: 'progress',
        scopeId: anchor,
        severity: SEVERITY.SUCCESS,
        category: CATEGORY.PROCESS,
        title: `${label} entries outperform by ${fmtPct(Math.abs(gap))}`,
        detail: `${w.count} trades with ${label} → ${fmtPct(w.wr)} WR (avg ${fmtR(w.avgR)}). ${wo.count} trades without → ${fmtPct(wo.wr)} WR (avg ${fmtR(wo.avgR)}). Pattern is a real edge for you over the last ${windowDays}D.`,
        action: `When you spot a ${label} setting up, take it. The data says it's earning its keep.`,
        evidence: { patternType, withPattern: w, withoutPattern: wo, gap, windowDays },
        confidence: 0.85,
        priority: 70,
      }));
    } else {
      // Pattern underperforms — surface as warning
      observations.push(makeObservation({
        id: `${key}:${anchor}:${patternType}:underperforms`,
        analyzer: key,
        scope: 'progress',
        scopeId: anchor,
        severity: SEVERITY.WARNING,
        category: CATEGORY.PROCESS,
        title: `${label} entries UNDERperform by ${fmtPct(Math.abs(gap))}`,
        detail: `${w.count} trades with ${label} → ${fmtPct(w.wr)} WR (avg ${fmtR(w.avgR)}). ${wo.count} trades without → ${fmtPct(wo.wr)} WR (avg ${fmtR(wo.avgR)}). The literature claims ${label} is an edge — your data says the opposite.`,
        action: `Audit your last 5 ${label} trades. Either (a) you're misidentifying the pattern, (b) it works in other markets but not yours, or (c) your management of these specific setups is leaking edge.`,
        evidence: { patternType, withPattern: w, withoutPattern: wo, gap, windowDays },
        confidence: 0.78,
        priority: 74,
      }));
    }
  }

  return observations.length > 0 ? observations : null;
};
